import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  activateReferralLead,
  fetchAssignableOfficers,
  getReferralLead,
  rejectReferralLead,
} from "../referralLeadsApi";
import ClientProfileLink from "../ClientProfileLink";

/**
 * One referred lead: who sent it, what the client filled in, and the activation decision.
 * Rejection needs a reason inline — the partner reads it to fix and resubmit. Someone with
 * officer-assignment rights can hand the new client straight to a case officer while
 * activating; the officer is notified and the assignment is tracked.
 */
const ReferredClientDrawer = ({
  leadId,
  canActivate,
  canReject,
  canAssignOfficer,
  onClose,
  onChanged,
}) => {
  const open = Boolean(leadId);
  const [rejectReason, setRejectReason] = useState("");
  const [officerId, setOfficerId] = useState(null);
  const [activateAsk, setActivateAsk] = useState(false);
  const [rejectAsk, setRejectAsk] = useState(false);

  // Forced fresh on open: the queue behind this drawer may have moved on.
  const {
    data: lead,
    isLoading,
    isError,
    refetch,
  } = useQuery(["referral-lead", leadId], () => getReferralLead(leadId), {
    enabled: open,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (open) {
      setRejectReason("");
      setOfficerId(null);
      setActivateAsk(false);
      setRejectAsk(false);
      refetch();
    }
  }, [open, leadId, refetch]);

  const { data: officers = [] } = useQuery(
    ["referral-assignable-officers"],
    fetchAssignableOfficers,
    { enabled: open && canActivate && canAssignOfficer }
  );

  const chosenOfficer = officers.find((officer) => officer.id === officerId) || null;

  const done = () => {
    onChanged?.();
    onClose?.();
  };

  const activateMutation = useMutation(
    ({ id, caseOfficerId }) => activateReferralLead(id, caseOfficerId ? { caseOfficerId } : {}),
    {
      onSuccess: () => {
        toast.success("Lead activated into a client");
        setActivateAsk(false);
        refetch();
        done();
      },
      onError: (err) =>
        toast.error(
          err?.response?.data?.errorMessage || err?.response?.data?.message || "Could not activate"
        ),
    }
  );

  const rejectMutation = useMutation(({ id, reason }) => rejectReferralLead(id, reason), {
    onSuccess: () => {
      toast.success("Lead returned to the partner");
      setRejectAsk(false);
      refetch();
      done();
    },
    onError: (err) =>
      toast.error(
        err?.response?.data?.errorMessage || err?.response?.data?.message || "Could not reject"
      ),
  });

  const Field = ({ label, value }) => (
    <div className="flex gap-3 py-1.5 border-b border-app-border last:border-0">
      <div className="w-36 shrink-0 text-sm text-app-text-muted">{label}</div>
      <div className="text-sm font-medium break-words">{value || "—"}</div>
    </div>
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className="w-full sm:w-[480px] max-w-[100vw] h-full flex flex-col bg-app-surface text-app-text">
        <div className="px-4 py-3 border-b border-app-border">
          <Typography variant="h6" className="font-semibold">
            Referred lead
          </Typography>
          {lead?.partnerName ? (
            <Typography variant="body2" color="text.secondary">
              Sent by {lead.partnerName}
            </Typography>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <CircularProgress />
            </div>
          ) : isError || !lead ? (
            <Typography color="error">Could not load this lead.</Typography>
          ) : (
            <>
              {lead.state === "REJECTED" && lead.rejectionReason ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Returned: {lead.rejectionReason}
                </Alert>
              ) : null}
              <Field
                label="Name"
                value={
                  <ClientProfileLink clientId={lead.convertedClientId}>
                    {lead.name}
                  </ClientProfileLink>
                }
              />
              <Field label="Email" value={lead.email} />
              <Field label="Phone" value={lead.number} />
              <Field label="Address" value={lead.address} />
              <Field label="Nationality" value={lead.nationality} />
              <Field label="Gender" value={lead.gender} />
              <Field label="Date of birth" value={lead.dob} />
              <Field label="Passport" value={lead.passportNumber} />
              <Field label="Interested in" value={(lead.interestedServices || []).join(", ")} />
              <Field label="Comments" value={lead.comments} />
              <Field label="State" value={lead.state} />
              <Field
                label="Sent"
                value={lead.sentToOrgAt ? new Date(lead.sentToOrgAt).toLocaleString() : null}
              />
              {lead.convertedClientId ? (
                <Field
                  label="Client id"
                  value={
                    <ClientProfileLink clientId={lead.convertedClientId}>
                      {String(lead.convertedClientId)}
                    </ClientProfileLink>
                  }
                />
              ) : null}

              {canReject && lead.state === "SENT_TO_ORG" ? (
                <div className="mt-4">
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    label="Rejection reason (required to return)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>
              ) : null}

              {canActivate && canAssignOfficer && lead.state === "SENT_TO_ORG" ? (
                <div className="mt-4">
                  <Autocomplete
                    options={officers}
                    getOptionLabel={(option) => option?.name || ""}
                    value={chosenOfficer}
                    onChange={(_, value) => setOfficerId(value?.id ?? null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Case officer (optional)"
                        helperText="Admin, account or super-admin users only. The officer is notified."
                      />
                    )}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-app-border">
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          {canReject && lead?.state === "SENT_TO_ORG" ? (
            <Button
              variant="outlined"
              color="error"
              disabled={!rejectReason.trim() || rejectMutation.isLoading}
              onClick={() => setRejectAsk(true)}
            >
              Return to partner
            </Button>
          ) : null}
          {canActivate && lead?.state === "SENT_TO_ORG" ? (
            <Button variant="contained" onClick={() => setActivateAsk(true)}>
              Activate
            </Button>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={activateAsk}
        title="Activate this lead?"
        message={`${lead?.name} becomes a real client, permanently attributed to ${
          lead?.partnerName
        }.${
          chosenOfficer ? ` ${chosenOfficer.name} is assigned as case officer and notified.` : ""
        }`}
        confirmLabel="Activate"
        loading={activateMutation.isLoading}
        onConfirm={() => activateMutation.mutate({ id: lead.id, caseOfficerId: officerId })}
        onCancel={() => setActivateAsk(false)}
      />

      <ConfirmDialog
        open={rejectAsk}
        title="Return this lead?"
        message={`The partner sees the reason and can fix and resubmit: "${rejectReason.trim()}"`}
        confirmLabel="Return to partner"
        loading={rejectMutation.isLoading}
        onConfirm={() => rejectMutation.mutate({ id: lead.id, reason: rejectReason.trim() })}
        onCancel={() => setRejectAsk(false)}
      />
    </Drawer>
  );
};

export default ReferredClientDrawer;
